<!-- src/components/settings/account/ChangePasswordDialog.svelte -->

<script>
  import { createEventDispatcher, tick } from 'svelte';
  import { CheckCircle2, KeyRound, X } from 'lucide-svelte';
  import ApiClient from '$lib/api';

  const dispatch = createEventDispatcher();
  const api = new ApiClient();

  export let visible = false;
  export let saving = false;
  export let accountId = null;

  let wasVisible = false;
  let currentPasswordInput;
  let newPasswordInput;

  let step = 1;
  let verifying = false;
  let showPassword = false;
  let passwordMinLength = 3;

  let form = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };

  let errors = {};

  $: strength = getStrength(form.newPassword);
  $: strengthColor =
    strength.value === 100
      ? 'bg-emerald-500'
      : strength.value >= 66
        ? 'bg-amber-500'
        : 'bg-red-500';

  $: passwordHints = [
    {
      label: `At least ${passwordMinLength} characters`,
      done: form.newPassword.length >= passwordMinLength
    },
    {
      label: 'Uppercase letter A-Z',
      done: /[A-Z]/.test(form.newPassword)
    },
    {
      label: 'Lowercase letter a-z',
      done: /[a-z]/.test(form.newPassword)
    },
    {
      label: 'Number 0-9',
      done: /\d/.test(form.newPassword)
    },
    {
      label: 'Symbol such as !@#$%',
      done: /[^A-Za-z0-9]/.test(form.newPassword)
    }
  ];

  $: if (showPassword && form.confirmPassword) {
    form.confirmPassword = '';
  }

  $: if (visible && !wasVisible) {
    wasVisible = true;
    focusCurrentPassword();
  }

  $: if (!visible && wasVisible) {
    wasVisible = false;
    reset();
  }

  function getStrength(password) {
    if (!password) {
      return {
        label: '',
        value: 0
      };
    }

    let score = 0;

    if (password.length >= passwordMinLength) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) {
      return {
        label: 'Weak',
        value: 33
      };
    }

    if (score <= 4) {
      return {
        label: 'Medium',
        value: 66
      };
    }

    return {
      label: 'Strong',
      value: 100
    };
  }

  async function focusCurrentPassword() {
    await tick();

    setTimeout(() => {
      currentPasswordInput?.focus();
    }, 100);
  }

  async function focusNewPassword() {
    await tick();

    setTimeout(() => {
      newPasswordInput?.focus();
    }, 100);
  }

  function reset() {
    step = 1;
    verifying = false;
    showPassword = false;

    form = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };

    errors = {};
  }

  function close() {
    if (saving || verifying) return;

    reset();

    dispatch('close');
  }

  async function verifyCurrentPassword() {
    errors = {};

    if (!form.currentPassword) {
      errors.currentPassword = 'Enter your current password to continue';
      focusCurrentPassword();
      return;
    }

    if (!accountId) {
      errors.currentPassword = 'User information is missing';
      return;
    }

    try {
      verifying = true;

      const result = await api.post(`/api/accounts/${accountId}/verify-password`, {
        currentPassword: form.currentPassword
      });

      if (!result.success) {
        errors.currentPassword = result.message || 'Current password is incorrect';
        focusCurrentPassword();
        return;
      }

      step = 2;
      errors = {};
      focusNewPassword();
    } catch (err) {
      errors.currentPassword = err?.message || 'Could not verify current password';
      focusCurrentPassword();
    } finally {
      verifying = false;
    }
  }

  function validateNewPassword() {
    errors = {};

    if (!form.newPassword) {
      errors.newPassword = 'Enter a new password';
    } else if (form.newPassword.length < passwordMinLength) {
      errors.newPassword = `Password must be at least ${passwordMinLength} characters`;
    }

    if (!showPassword) {
      if (!form.confirmPassword) {
        errors.confirmPassword = 'Confirm your new password';
      } else if (form.newPassword !== form.confirmPassword) {
        errors.confirmPassword = 'Password confirmation does not match';
      }
    }

    return Object.keys(errors).length === 0;
  }

  function save() {
    if (saving || !validateNewPassword()) {
      return;
    }

    dispatch('save', {
      currentPassword: form.currentPassword,
      newPassword: form.newPassword
    });
  }

  function submit() {
    if (step === 1) {
      verifyCurrentPassword();
      return;
    }

    save();
  }
</script>

{#if visible}
  <div class="fixed inset-0 z-[9999] bg-black/35 flex items-center justify-center p-4">
    <div class="w-full max-w-lg bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden">
      <div class="h-14 px-5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="h-9 w-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
            <KeyRound size={18} />
          </div>

          <div>
            <h2 class="text-base font-semibold text-slate-900">Change Password</h2>
            <p class="text-xs text-slate-500">
              {step === 1 ? 'Verify your current password first' : 'Create your new password'}
            </p>
          </div>
        </div>

        <button
          type="button"
          class="h-8 w-8 rounded-lg border border-transparent hover:border-slate-200 hover:bg-white flex items-center justify-center disabled:opacity-50"
          disabled={saving || verifying}
          on:click={close}>
          <X size={16} />
        </button>
      </div>

      <form on:submit|preventDefault={submit}>
        <div class="p-5 space-y-4">
          <div class="flex items-center gap-2 text-xs text-slate-500">
            <span class="h-6 w-6 rounded-full flex items-center justify-center {step === 1 ? 'bg-blue-600 text-white' : 'bg-emerald-100 text-emerald-700'}">
              {#if step === 1}
                1
              {:else}
                <CheckCircle2 size={14} />
              {/if}
            </span>
            <span>Current password</span>
            <span class="h-px flex-1 bg-slate-200"></span>
            <span class="h-6 w-6 rounded-full flex items-center justify-center {step === 2 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}">
              2
            </span>
            <span>New password</span>
          </div>

          {#if step === 1}
            <div>
              <label for="current-password" class="block text-sm font-medium text-slate-700 mb-1.5">
                Current Password
              </label>

              <input
                id="current-password"
                bind:this={currentPasswordInput}
                bind:value={form.currentPassword}
                type={showPassword ? 'text' : 'password'}
                autocomplete="current-password"
                placeholder="Enter current password"
                class="w-full h-11 px-3.5 text-sm border border-slate-300 rounded-lg outline-none placeholder:text-sm placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500"
                disabled={saving || verifying} />

              {#if errors.currentPassword}
                <div class="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errors.currentPassword}
                </div>
              {/if}
            </div>
          {:else}
            <div class="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              Current password verified. Enter your new password below.
            </div>

            <div>
              <label for="new-password" class="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>

              <input
                id="new-password"
                bind:this={newPasswordInput}
                bind:value={form.newPassword}
                type={showPassword ? 'text' : 'password'}
                autocomplete="new-password"
                placeholder="Enter new password"
                class="w-full h-11 px-3.5 text-sm border border-slate-300 rounded-lg outline-none placeholder:text-sm placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500"
                disabled={saving} />

              {#if errors.newPassword}
                <div class="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errors.newPassword}
                </div>
              {/if}
            </div>

            {#if !showPassword}
              <div>
                <label for="confirm-password" class="block text-sm font-medium text-slate-700 mb-1.5">
                  Confirm Password
                </label>

                <input
                  id="confirm-password"
                  bind:value={form.confirmPassword}
                  type="password"
                  autocomplete="new-password"
                  placeholder="Confirm new password"
                  class="w-full h-11 px-3.5 text-sm border border-slate-300 rounded-lg outline-none placeholder:text-sm placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500"
                  disabled={saving} />

                {#if errors.confirmPassword}
                  <div class="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {errors.confirmPassword}
                  </div>
                {/if}
              </div>
            {/if}

            {#if form.newPassword}
              <div class="space-y-2">
                <div>
                  <div class="flex justify-between text-xs text-slate-500 mb-1.5">
                    <span>Password strength</span>

                    <span class="font-medium text-slate-700">{strength.label}</span>
                  </div>

                  <div class="h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div class="h-full {strengthColor} transition-all" style="width:{strength.value}%"></div>
                  </div>
                </div>

                <div class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div class="text-xs font-medium text-slate-600 mb-2">Use these characters for a stronger password</div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {#each passwordHints as hint}
                      <div class="flex items-center gap-2 text-xs {hint.done ? 'text-emerald-700' : 'text-slate-500'}">
                        <span class="h-1.5 w-1.5 rounded-full {hint.done ? 'bg-emerald-500' : 'bg-slate-300'}"></span>
                        <span>{hint.label}</span>
                      </div>
                    {/each}
                  </div>
                </div>
              </div>
            {/if}
          {/if}

          <label for="show-password" class="flex items-center gap-2 text-sm text-slate-700">
            <input id="show-password" type="checkbox" bind:checked={showPassword} disabled={saving || verifying} />

            Show password
          </label>
        </div>

        <div class="px-5 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button
            type="button"
            class="h-10 px-4 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50 disabled:opacity-50"
            disabled={saving || verifying}
            on:click={close}>
            Cancel
          </button>

          <button
            type="submit"
            class="h-10 px-5 rounded-lg bg-blue-600 text-sm text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={saving || verifying}>
            {#if step === 1}
              {verifying ? 'Verifying...' : 'Verify'}
            {:else}
              {saving ? 'Saving...' : 'Save'}
            {/if}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
